import { serviceDomain } from "@/constants/serviceDomain";
import {
  convertToJournal,
  convertToJournalAiAnalysis,
  convertToJournalReflection,
  Journal,
  JournalAiAnalysis,
  journalAiAnalysisCollectionKey,
  JournalAiAnalysisData,
  journalCollectionKey,
  JournalData,
  JournalReflection,
  journalReflectionCollectionKey
} from "@/entities/journal";
import { serviceDomainsCollectionKey } from "@/entities/school_ai_admin";
import {
  convertToJournalAnalysisPrompt,
  JournalAnalysisPrompt,
  journalAnalysisPromptCollectionKey
} from "@/entities/school_ai_analysis";
import { TutorConfig } from "@/entities/tutor";
import { chunkArray } from "@/utils/array";
import { isAdminOrOwnerOfSchool } from "@/utils/tutor";
import dayjs from "dayjs";
import firebase from "firebase/app";
import "firebase/firestore";

type ResponseReflection = {
  title: string;
  content: string | number;
};

type ResponseRecord = {
  date: number;
  studentName: string;
  studentId: string;
  journalTitle: string;
  journalId: string;
  reflections: ResponseReflection[];
};

type ResponseComment = {
  date: number;
  studentName: string;
  studentId: string;
  journalTitle: string;
  journalId: string;
  comment: string;
};

export async function getJournal(
  journalRef: firebase.firestore.DocumentReference
): Promise<Journal | null> {
  const snapshot = await journalRef.get();
  if (!snapshot.exists) return null;
  const data = snapshot.data();
  if (!data || data.isDeleted) return null;

  return convertToJournal(data, snapshot.ref);
}

export async function getJournalsOfSchool(
  schoolRef: firebase.firestore.DocumentReference,
  tutorConfig: TutorConfig
): Promise<Journal[]> {
  if (isAdminOrOwnerOfSchool(tutorConfig)) {
    // とりあえずオーナー権限はすべてのジャーナルを取る方針
    const snapshot = await schoolRef
      .collection(journalCollectionKey)
      .where("isDeleted", "==", false)
      .get();
    if (snapshot.empty) return [];

    return snapshot.docs
      .filter(doc => doc.data())
      .map(doc => convertToJournal(doc.data(), doc.ref));
  } else {
    const [myJournals, publicJournals] = await Promise.all([
      schoolRef
        .collection(journalCollectionKey)
        .where("isDeleted", "==", false)
        .where("createdBy", "==", tutorConfig.data.id)
        .get(),
      schoolRef
        .collection(journalCollectionKey)
        .where("isDeleted", "==", false)
        .where("tutorAccess", "==", "public")
        .get()
    ]);

    const journals = [
      ...myJournals.docs
        .filter(doc => doc.exists && doc.data())
        .map(doc => convertToJournal(doc.data(), doc.ref)),
      ...publicJournals.docs
        .filter(doc => doc.exists && doc.data())
        .map(doc => convertToJournal(doc.data(), doc.ref))
    ];

    // 重複を除く
    return [
      ...new Map(journals.map(journal => [journal.ref.id, journal])).values()
    ];
  }
}

export async function createJournal(
  schoolRef: firebase.firestore.DocumentReference,
  data: JournalData
): Promise<firebase.firestore.DocumentReference> {
  const ref = await schoolRef.collection(journalCollectionKey).add(data);

  return ref;
}

export async function updateJournal(
  journalRef: firebase.firestore.DocumentReference,
  data: Partial<JournalData>
) {
  await journalRef.update(data);
}

export async function deleteJournal(
  journalRef: firebase.firestore.DocumentReference,
  userUid: string
) {
  const data: Partial<JournalData> = {
    isDeleted: true,
    deletedAt: dayjs().unix(),
    deletedBy: userUid
  };

  await journalRef.update({ ...data });
}

export async function getReflectionsOfJournal(
  journalRef: firebase.firestore.DocumentReference,
  tutorConfig: TutorConfig
): Promise<JournalReflection[]> {
  const MAX_FILTER_SIZE = 10;

  const classroomIds = tutorConfig.data.classrooms.map(_ => _.id);
  const chunks = chunkArray(classroomIds, MAX_FILTER_SIZE);

  const snapshots = await Promise.all(
    chunks.map(chunk =>
      journalRef
        .collection(journalReflectionCollectionKey)
        .where("submitterDetail.classroomId", "in", chunk)
        .where("isParentJournalDeleted", "==", false)
        .get()
    )
  );

  const allDocs = snapshots.flatMap(snapshot => snapshot.docs);

  return allDocs
    .filter(doc => doc.exists && doc.data())
    .map(doc => convertToJournalReflection(doc.data(), doc.ref));
}

export async function getAnalysisOfJournal(
  journalRef: firebase.firestore.DocumentReference,
  tutorId: string
): Promise<JournalAiAnalysis | null> {
  const snapshot = await journalRef
    .collection(journalAiAnalysisCollectionKey)
    .doc(tutorId)
    .get();

  if (!snapshot.exists || !snapshot.data()) {
    return null;
  }

  return convertToJournalAiAnalysis(snapshot.data()!, snapshot.ref);
}

export async function setAnalysisOfJournal(
  ref: firebase.firestore.DocumentReference,
  content:
    | {
        isUpdating: true;
        data: Omit<JournalAiAnalysisData, "createdAt">;
      }
    | {
        isUpdating: false;
        data: JournalAiAnalysisData;
      }
) {
  const { data } = content;

  await ref.set(data, { merge: true });
}

export async function getJournalRecords(studentIds: string[]) {
  const call = firebase
    .app()
    .functions("asia-northeast1")
    .httpsCallable("get_students_journals_records");

  const res = await call({
    studentIds
  });

  if (!res.data.success) {
    throw new Error("unexpected error occurred");
  }

  if (!Array.isArray(res.data.records)) {
    throw new Error("records is not array");
  }

  if (!Array.isArray(res.data.comments)) {
    throw new Error("comments is not array");
  }

  return res.data as {
    success: boolean;
    records: ResponseRecord[];
    comments: ResponseComment[];
    empty?: boolean;
  };
}

export async function getJournalComments(studentIds: string[]) {
  const call = firebase
    .app()
    .functions("asia-northeast1")
    .httpsCallable("get_students_journals_comments");

  const res = await call({
    studentIds
  });

  if (!res.data.results) {
    throw new Error("results not found");
  }

  if (!Array.isArray(res.data.results)) {
    throw new Error("results is not array");
  }

  return res.data.results;
}

export async function getJournalAnalysisPrompt(): Promise<JournalAnalysisPrompt | null> {
  const snapshot = await firebase
    .firestore()
    .collection(serviceDomainsCollectionKey)
    .doc(serviceDomain)
    .collection(journalAnalysisPromptCollectionKey)
    .get();

  if (snapshot.empty || !snapshot.docs[0].exists || !snapshot.docs[0].data()) {
    return null;
  }

  return convertToJournalAnalysisPrompt(
    snapshot.docs[0].data(),
    snapshot.docs[0].ref
  );
}
